/**
 * The HUD's generated art — the action-bar button pictures and the player
 * panel's emblems.
 *
 * Janeks: *"No CSS drawings, use Higgsfield to create realistic items for
 * sections."* Built by `assets/generated_items/build_hud_art.py` from the
 * Higgsfield masters in `assets/generated_items/hud-art/`.
 *
 * Globbed rather than imported one by one, with the same query options as
 * `paperdoll.ts` and `hud.ts` — a bare runtime path would 404 in production,
 * because Vite hashes the asset name. A file that has not been built yet is
 * simply absent from the map, and every caller falls back to what it drew
 * before, so a missing picture degrades instead of breaking the HUD.
 */
const MODULES = import.meta.glob<string>('../assets/ui/hud/*.webp', {
  eager: true, query: '?url', import: 'default',
});

const ART: Record<string, string> = {};
for (const [path, url] of Object.entries(MODULES)) {
  ART[path.slice(path.lastIndexOf('/') + 1, -'.webp'.length)] = url;
}

/** The hashed URL of one piece of art, or '' when it is not built. */
export function hudArtUrl(name: string): string {
  return ART[name] ?? '';
}

/** Which picture each action-bar icon name wears. */
export const ACTION_ART: Record<string, string> = {
  backpack: 'ico_backpack',
  journal: 'ico_journal',
  tag: 'ico_tag',
  map: 'ico_map',
  swords: 'ico_blades',
  house: 'ico_home',
  speech: 'ico_talk',
};

/** The picture for an action-bar icon, as an `<img>`, or '' to fall back to the drawn glyph. */
export function actionArtHtml(icon: string): string {
  const url = hudArtUrl(ACTION_ART[icon] ?? '');
  return url ? `<img class="bv-ab-art" src="${url}" alt="" aria-hidden="true" draggable="false" />` : '';
}

/** A stat emblem (`hp`, `ar`, `en`, `xp`) as an `<img>`, or the plain label when the art is missing. */
export function statEmblemHtml(stat: 'hp' | 'ar' | 'en' | 'xp', label: string): string {
  const url = hudArtUrl(`em_${stat}`);
  return url
    ? `<img class="bv-hud-emblem" src="${url}" alt="${label}" title="${label}" draggable="false" />`
    : `<span class="bv-hud-label">${label}</span>`;
}
